import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import type { DeepNonNullable } from "utility-types";

import type { authorRelatedTopicsAuthor$key } from "scmp-app/queries/__generated__/authorRelatedTopicsAuthor.graphql";

import { Container, Label, TopicLink } from "./styles";

type Props = {
  reference: authorRelatedTopicsAuthor$key;
};

export const AuthorRelatedTopics: FunctionComponent<Props> = ({ reference }) => {
  const author = useFragment(
    graphql`
      fragment authorRelatedTopicsAuthor on Author
      @argumentDefinitions(
        after: { type: "String" }
        applicationIds: { type: "[String]", defaultValue: [] }
        count: { type: "Int", defaultValue: 30 }
      ) {
        contents(
          after: $after
          first: $count
          filter: { applicationIds: $applicationIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              topics {
                entityId
                name
                ...entityLink
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const mainTopics = useMemo(() => {
    type Item = DeepNonNullable<
      typeof author
    >["contents"]["edges"][number]["node"]["topics"][number];

    const mainTopics = author.contents?.edges.reduce<Record<string, Item>>((mainTopics, item) => {
      const mainTopic = first(item.node.topics);
      if (mainTopic) {
        mainTopics[mainTopic.entityId] = mainTopic;
      }
      return mainTopics;
    }, {});

    if (!mainTopics) return [];

    return Object.values(mainTopics).slice(0, 10);
  }, [author]);

  return (
    <Container>
      <Label>{mainTopics.length === 1 ? "Related Topic" : "Related Topics"}</Label>
      {mainTopics.length > 0
        ? mainTopics.map(topic => (
            <TopicLink key={topic.entityId} reference={topic}>
              {topic.name}
            </TopicLink>
          ))
        : `No Related Topics`}
    </Container>
  );
};

AuthorRelatedTopics.displayName = "AuthorRelatedTopics";
